/**
 * Authentication Functions
 */

import { Injectable } from '@angular/core';
import { JwtHelper } from 'angular2-jwt';
import { Headers, URLSearchParams, RequestOptions } from '@angular/http';
import { Router } from '@angular/router';
// import { StateService } from '@uirouter/angular';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material';

import { AdminService } from './../../common-services/admin.service';
import { SocketService } from './../../common-services/socket.service';
import { SubscriptionManagementService } from './../../common-services/subscription-management.service';
import { UserService } from './../../common-services/user.service';
import { CommonRequestService } from './../../common-services/common-request.service';
import { AppSettings } from './../../app.settings';
import { CommonGfoService } from './../../common-services/common-gfo.service';
import { RefWindowService } from '../../common-services/ref-window.service';

export type AuthTypeValue = 'AD' | 'Esri' | 'LB';
export interface GetAuthTypeOutput {
	cusAuth: AuthType;
	userName: string;
	userSignupState: 'ACTIVE'
}
export type AuthType = {
	type: 'LB';
	secret: null;
} | {
	type: 'AD';
	secret: {
		baseDN: string; // 'dc=gisticinc,dc=lan'
		domain: string; // 'gisticinc.lan'
		encrypted: boolean;
		password: string;
		url: string; // ldap://72.215.195.69
		username: string;
	}
} | {
	type: 'Esri';
	secret: {
		clientId: string;
		url: string;
	}
};

export interface UpdateCustomerProps {
	customerShortName?: string,
	customerLongName?: string,
	customerType?: string,
	fcNamePrefix?: string,
	auth?: string,
	discripAgId?: string,
	customerCentroid?: string,
	customerCounties?: string[],
	customerState?: string;
	customerCountry?: string;
}


@Injectable()
export class AuthenticationService {
	private jwtHelper: JwtHelper = new JwtHelper();
	// private tokenUpdateInterval;

	public authTypeOutput: GetAuthTypeOutput;

	constructor(
		private dialog: MatDialog,
		private router: Router,
		// private stateService: StateService,
		private toastr: ToastrService,
		private adminService: AdminService,
		private socketService: SocketService,
		private subsManageService: SubscriptionManagementService,
		private userService: UserService,
		private requestService: CommonRequestService,
		private gfoService: CommonGfoService,
		private windowRef: RefWindowService
	) {}

	login(email, password, successCallback?, errorCallback?) {
		const body = {
			email: email,
			password: password
		};
		return this.requestService.requestPost(AppSettings.serviceRootUrl + 'admin/login', body, successCallback, errorCallback, true);
	}
	logout(): void {
		// Save current state info to localSession
		sessionStorage.setItem('prevState', JSON.stringify({
			path: this.router.url
			// name: this.stateService.current.name,
			// params: {}
		}));
		// Remove token
		this.userService.removeToken();
		// Disconnect socket
		this.socketService.disconnectSocket();
		// Clear current user data // So that when login with another email, the app won't keep previous user's data
		this.adminService.userSubscriptions = null;
		// Clear subscriptions
		this.adminService.subscriptions.next(null);
		this.subsManageService.apps = null;
		// Close all modal window
		this.dialog.closeAll();
		// Navigate to login
		this.router.navigate(['/auth-direct']);
		// this.stateService.go('login');
	}
	// Register a new agency
	// This function should not be called alone. Call together with sendVerifyEmail
	// returns customerId & userId
	registerAgency(agencyNameShort, adminEmail, password, agencyNameLong?) {
		return new Promise<{cusId;userId;}>((resolve, reject) => {
			const body = {
				customerLongName: agencyNameLong,
				customerShortName: agencyNameShort,
				email: adminEmail,
				password: password
			};
			this.requestService.requestPost(AppSettings.serviceRootUrl + 'admin/customer', body, rsp => {
				const data = rsp.data[0];
				if (data) {
					resolve({
						cusId: data.cus_id,
						userId: data.usr_id
					})
				} else {
					resolve(null);
				}
			}, err => reject(err), true);
		});
	}
	updateCustomer(props: UpdateCustomerProps, cusId?, skipAuth?: boolean) {
		return new Promise((resolve, reject) => {
			cusId = cusId || this.userService.getUserInfo().customerId;
			const body: any = {
				// customerShortName: shortName,
				customerId: cusId
			};
			// if (longName) {
			// 	body.customerLongName = longName;
			// }
			const attrs = ['customerShortName',
				'customerLongName',
				'customerId',
				'parentId',
				'customerType',
				'fcNamePrefix',
				'auth',
				'discripAgId',
				'customerCentroid',
				'customerCounties',
				'customerState',
				'customerCountry',
			];
			Object.keys(props).forEach(k => {
				if (attrs.indexOf(k) > -1 && props[k]) {
					if (k === 'customerCounties') {
						if (props[k].length) {
							body[k] = JSON.stringify(props[k])
						}
					} else {
						body[k] = props[k];
					}
				}
			})
			const url = 'admin/customer';
			this.requestService.requestPut(AppSettings.serviceRootUrl + url, body, (rsp) => {
				resolve(rsp.data);
			}, (err) => {
				this.toastr.error(err);
				reject(err);
			}, skipAuth);
		});
	}
	// Send an link to the email regesitered as admin for verification
	// User will use a link in that email to activate their newly-created account
	sendVerifyEmail(email, agencyName, initialApp?: string) {
		return new Promise((resolve, reject) => {
			const body: any = {
				email: email,
				agency: agencyName, // Agency name to display in the email
				emailLinkRoot: AppSettings.rootLink + 'auth/register-verify',
				approveEmailLinkRoot: AppSettings.rootLink + 'auth/register-approve', // TODO!!!
			};
			if (initialApp) {
				body.initialApplication = initialApp;
			}
			this.requestService.requestPost(AppSettings.serviceRootUrl + 'admin/register', body, rsp => resolve(rsp), err => reject(err), true);
	
		});
	}
	// User veriry their email
	verifyUserEmail(token: string) {
		return new Promise((resolve, reject) => {
			const info = this.jwtHelper.decodeToken(token);
			const url = AppSettings.serviceRootUrl + 'admin/approve';
			const body: any = {
				email: info.email,
				agency: info.agency,
				emailLinkRoot: AppSettings.rootLink + 'auth/register-approve',
			};
			if (info.initialApplication) {
				body.initialApplication = info.initialApplication;
			}
			// const headers = new Headers({
			// 	Authorization: 'Bearer' + token
			// });
			this.requestService.requestPost(url, body, rsp => resolve(rsp), err => reject(err), true);
		});
	}

	// Application Administrator approve newly-registered customer
	approveRegistration(token: string, approve: boolean, successCallback?, errorCallback?) {
		const info = this.jwtHelper.decodeToken(token);
		// Update user status (activate user)
		const urlUpdate = AppSettings.serviceRootUrl + 'admin/user';
		const bodyUpdate = {
			email: info.email,
			state: 'ACTIVE'
		};
		this.requestService.requestPut(urlUpdate, bodyUpdate, (rsp) => {
			// Send an success email to user
			const urlSend = AppSettings.serviceRootUrl + 'admin/registerEmail';
			const bodySend = {
				email: info.email,
				approve: approve
			};
			this.requestService.requestPost(urlSend, bodySend, successCallback, (err) => {
				this.toastr.error('Failed to send user an email.');
				if (errorCallback) { errorCallback(err); }
			}, true);
		}, (err) => {
			this.toastr.error('Failed to approve email.');
			if (errorCallback) { errorCallback(err); }
		}, true);

		// const headers = new Headers({
		// 	Authorization: 'Bearer' + token
		// });
	}
	public decodeToken(token: string) {
		return this.jwtHelper.decodeToken(token);
	}
	// // Veriry Link and the newly registered admin set the password
	// registerVerify(password, token, successCallback?, errorCallback?) {
	// 	const info = this.jwtHelper.decodeToken(token);
	// 	const body = {
	// 		email: info.usr_email,
	// 		password: password,
	// 		// application: AppSettings.appName,
	// 		state: 'ACTIVE' // 1025 // TODO
	// 	};
	// 	const headers = new Headers({
	// 		Authorization: 'Bearer ' + token
	// 	});
	// 	this.requestService.requestPut(AppSettings.serviceRootUrl + 'admin/user', body, successCallback, errorCallback, true, headers);
	// }

	// Invite user: send an email to the invited user
	inviteUser(email: string, successCallback?, errorCallback?): void {
		const body = {
			email: email,
			emailLinkRoot: AppSettings.rootLink + 'auth/set-pw'
		};
		this.requestService.requestPost(AppSettings.serviceRootUrl + 'admin/invite', body, successCallback, errorCallback);
	}

	// Send an email to account to reset password
	retrievePw(email: string, successCallback?, errorCallback?) {
		const body = {
			email: email,
			emailLinkRoot: AppSettings.rootLink + 'auth/reset-pw'
		};
		return this.requestService.requestPost(AppSettings.serviceRootUrl + 'admin/reset', body, successCallback, errorCallback, true);
	}
	// Reset password
	resetPw(password: string, token: string, isUpdateState?: boolean, userEmail?: string) {
		return new Promise((resolve, reject) => {
			if (!userEmail) {
				let info;
				try {
					info = this.jwtHelper.decodeToken(token);
				} catch (err) {
					this.toastr.error('Invalid Token');
					this.toastr.error(err.message);
					reject(err);
					return;
				}
				userEmail = info.usr_email;
			}
			const body: any = {
				email: userEmail,
				password: password,
				// application: AppSettings.appName
			};
			if (isUpdateState) {
				body.state = 'ACTIVE';
			}
			const headers = new Headers({
				Authorization: 'Bearer ' + token
			});
			return this.requestService.requestPut(AppSettings.serviceRootUrl + 'admin/user', body, rsp => resolve(rsp), err => reject(err), true, headers);
		});
	}
	activateUser(token: string, userEmail?: string) {
		return new Promise((resolve, reject) => {
			if (!userEmail) {
				let info;
				try {
					info = this.jwtHelper.decodeToken(token);
				} catch (err) {
					this.toastr.error('Invalid Token');
					this.toastr.error(err.message);
					reject(err);
					return;
				}
				userEmail = info.usr_email;
			}
			const body = {
				email: userEmail,
				state: 'ACTIVE'
			};
			const headers = new Headers({
				Authorization: 'Bearer ' + token
			});
			return this.requestService.requestPut(AppSettings.serviceRootUrl + 'admin/user', body, rsp => resolve(rsp), err => reject(err), true, headers);
		});
	}

	getAuthType(email: string, skipUpdate?: boolean) {
		return new Promise<GetAuthTypeOutput>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/authType';
			const params: URLSearchParams = new URLSearchParams();
			const options: RequestOptions = new RequestOptions();
			params.set('email', email);
			options.params = params;
			this.requestService.requestGet(url, options, (rsp: any) => {
				if (rsp.data && rsp.data[0]) {
					const data = rsp.data[0];
					const result: GetAuthTypeOutput = {
						cusAuth: data.cus_auth,
						userName: data.usr_name,
						userSignupState: data.usr_signup_state
					}
					if (!skipUpdate) {
						this.authTypeOutput = result;
					}
					resolve(result);
				} else {
					this.toastr.info('Can not find user.');
					reject();
				}
			}, err => reject(err), true);
		});
	}
	public getAuthTypeFromToken(token) {
		return new Promise<GetAuthTypeOutput>(async (resolve, reject) => {
			try {
				const info = this.jwtHelper.decodeToken(token);
				if (info && info.usr_email) {
					const rsp = await this.getAuthType(info.usr_email);
					resolve(rsp);
					return;
				}
			} catch (err) {
				reject(err);
			}
			resolve();
		})
	}

	async authDirect(authType: AuthType) {
		switch (authType.type) {
			case 'LB':
			case 'AD':
				this.router.navigate(['auth/login']);
				break;
			case 'Esri':
				const secret = authType.secret;
				const url = secret.url + '?client_id=' + secret.clientId + '&response_type=token&redirect_uri=' + AppSettings.appRoot;
				this.windowRef.nativeWindow.open(url, '_self');
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/components/auth/auth.service.ts